import qs from '@xfe-team/utils/src/query-string-util';
import { invariant } from '@xfe-team/utils/src/error-utils';

/**
 * 由于较大可能会在短时间内对同一个 query 进行多次 parse, 所以可以缓存一次
 * @type {{}}
 */
const queryKeyPairCache = {};

/**
 * 匹配 query, 支持 3种 参数形式
 *
 * 1. 字符串, string, 如 matchQuery('aa'),
 * 此时会判断当前 query 是否存在 aa 这个key. 即
 *
 * matchQuery('?aa=123', 'a') // true
 *
 * 2. 对象, object, 支持复杂类型匹配, 如 {key, value, isIgnoreCase}
 * matchQuery('?aa=123', {key: /^aa$/, value: /^\d+$/})  // true
 * matchQuery('?aa=123', {key: /^aa$/})  // true
 * matchQuery('?aa=123', {value: /^\d+$/})  // true
 *
 * 3. 一维数组, string[] 或 object[], 支持 1, 2, 但以数组形式
 *
 * matchQuery('?aa=123', ['aa'])  // true
 * matchQuery('?aa=123', ['aa', 'bb'])  // false, 由于第二个参数为判断规则, 必须同时存在 a 和 b
 * matchQuery('?aa=123&bb=123', ['aa', 'bb'])  // true
 * matchQuery('?aa=123&bb=123&cc=456', ['aa', 'bb'])  // false, 参数多传入了一个 cc, 判断为不命中
 *
 * aa字段必须存在时, matchQuery('aa') 或 matchQuery(['aa'])
 *
 * @param {string} query -  query string url 字符
 * @param queryConfig
 * @param isIgnoreCase
 */
export default function matchQuery(query, queryConfig, { isIgnoreCase = true } = {}) {
  if (!query) {
    return {
      query: {}
    };
  }

  /**
   * 进行单次缓存
   */
  if (!queryKeyPairCache[query]) {
    queryKeyPairCache[query] = qs.parse(query.replace(/^\?/, ''));
  }

  if (!queryConfig) {
    return {
      query: queryKeyPairCache[query]
    };
  }

  if (!Array.isArray(queryConfig)) {
    queryConfig = [queryConfig];
  }

  /**
   * 解析 query string 后的对象
   */
  const queryParams = queryKeyPairCache[query];
  const queryParamsKeys = Object.keys(queryParams);
  const queryParamsValues = getObjectValues(queryParams);

  const isMatch = queryConfig.every(
    /**
     * @type {{key, value, isIgnoreCase}}
     */
    eachQueryConfig => {
      /**
       * matchQuery('?aa=123', 'a') 的字符串全匹配场景
       */

      if (isStringOrRegex(eachQueryConfig)) {
        return isStringOrRegexArrayCompare(queryParamsKeys, query, isIgnoreCase);
      } else if (typeof eachQueryConfig === 'object') {
        /**
         * matchQuery('?aa=123', {key: 'aa', value: /\d+/, isIgnoreCase: true}) 的对象复杂校验场景
         */
        /**
         * @type {boolean}
         */
        const eachQueryConfigIgnoreCase = eachQueryConfig.isIgnoreCase === undefined ? eachQueryConfig.isIgnoreCase : isIgnoreCase;
        const { key, value } = eachQueryConfig;
        if (key !== undefined || value !== undefined) {
          invariant(isStringOrRegex(key), `期望 eachQueryConfig.key 类型为[字符串]或[正则Regex], type of eachQueryConfig.key = ${getObjectType(key)}`);
          const isKeyValid = isStringOrRegexArrayCompare(queryParamsKeys, key, eachQueryConfigIgnoreCase);
          if (!isKeyValid) {
            return false;
          }
          return isStringOrRegexArrayCompare(queryParamsValues, value, eachQueryConfigIgnoreCase);
        } else {
          throw new Error('eachQueryConfig key 或 value 两者不能同时为空');
        }
      } else {
        throw new Error('TypeError: typeof eachQueryConfig = ' + getObjectValues(eachQueryConfig));
      }
    }
  );

  if (isMatch) {
    return null;
  }

  return {
    query: queryKeyPairCache[query]
  };
}

/**
 * 数组中是否包含, 主要支持以下 2 种场景
 *
 * isStringOrRegexArrayCompare(['aa', 'bb', 'cc'], 'aa', true) 场景, 判断 aa 是否在 ['aa', 'bb', 'cc'] 列表中
 * isStringOrRegexArrayCompare(['aa', 'bb', 'cc'], /^aa$/) 场景, 判断 aa 是否在 ['aa', 'bb', 'cc'] 列表中
 *
 * @param {string[]} sourceArray - 源字符串数组
 * @param {string|RegExp} compareValue - 比对值
 * @param {boolean} isIgnoreCase - 是否忽略大小写
 * @returns {boolean}
 */
function isStringOrRegexArrayCompare(sourceArray, compareValue, isIgnoreCase) {
  return sourceArray.some(sourceValue => {
    /**
     * sourceValue = 'aa', compareValue = 'aa', isIgnoreCase = true
     */
    if (typeof compareValue === 'string') {
      if (isIgnoreCase) {
        return sourceValue.toLowerCase() === compareValue.toLowerCase();
      } else {
        return sourceValue === compareValue;
      }
    } else if (compareValue instanceof RegExp) {
      /**
       * sourceValue = 'aa', compareValue = /^aa$/, isIgnoreCase = true, isIgnoreCase 在此场景中无作用
       */
      return compareValue.test(sourceValue);
    } else {
      throw new Error(`非法类型 type of compareValue = ${getObjectType(sourceValue)}`);
    }
  });
}

/**
 * 判断是 string 或 regex 类型
 * @param {*} val - 任意对象
 * @returns {boolean}
 */
function isStringOrRegex(val) {
  return typeof val === 'string' || val instanceof RegExp;
}

/**
 * 获取对象类型
 * @param {*} val - 任意对象
 * @returns {string}
 */
function getObjectType(val) {
  return Object.prototype.toString.call(val);
}

/**
 * 获取对象 values, 类型 Object.keys, 但主要
 * @param obj
 * @returns {*[]}
 */
function getObjectValues(obj) {
  return Object.keys(obj).map(key => obj[key]);
}
